<template>
  <!-- Tudo o que se escreve na tag — model-value, persistent, max-width, os ouvintes — cai no
       v-dialog pela herança de atributos: quem usa só troca o nome da tag. -->
  <!-- after-enter: a rede de segurança do `mounted`, caso o conteúdo ainda não existisse lá -->
  <v-dialog ref="dialog" @after-enter="smooth">
    <slot/>
  </v-dialog>
</template>

<script lang="ts">
import {Component, Vue, toNative} from 'vue-facing-decorator'
import {leaveOverlay, smoothHeight} from '@/utils/motion'

/**
 * O `v-dialog` das janelas que o pai abre e fecha com `v-if`, com a saída animada.
 *
 * É o arranjo de quase todo modal do app: a casca fixa `model-value` em true e o pai a monta e
 * desmonta. A entrada o Vuetify anima sozinho — o overlay nasce com `appear` —, mas a saída não
 * existe: desmontado, o `v-dialog` leva o Teleport junto no mesmo instante. Aqui, antes de ir, a
 * janela deixa uma cópia que faz a saída (ver o `leaveOverlay`).
 *
 * **Por que não fechar "direito"**, baixando o `model-value` e avisando o pai no `after-leave`:
 * quem fecha é o pai, e por caminhos que não passam pela janela — a escolha do AskDialog, o salvar
 * que termina, a navegação. Cada um teria de pedir o fechamento à janela em vez de tirar o `v-if`,
 * em todos os pais. Assim nenhum muda, e todo caminho de fechamento anima.
 *
 * Quem alterna o `v-model` de verdade (a busca, a tela cheia do diagrama) já tem a saída do
 * Vuetify e continua no `v-dialog`.
 */
@Component({name: 'ontos-dialog'})
class Dialog extends Vue {
  /** Para de acompanhar a altura. Null até a janela terminar de entrar. */
  private stopSmoothing: (() => void) | null = null

  get content(): HTMLElement | undefined {
    return (this.$refs.dialog as any)?.contentEl
  }

  /**
   * A janela muda de tamanho deslizando — a ficha que abre com a prévia e cresce quando a leitura
   * chega, o carregando que vira formulário. Ver o `smoothHeight`.
   *
   * Desde que o conteúdo existe, e não só depois de a janela terminar de entrar: a leitura da ficha
   * costuma chegar DURANTE a entrada, e o pulo aconteceria ali. A altura anima junto com o zoom da
   * entrada sem disputa — são propriedades diferentes do mesmo elemento.
   *
   * O VOverlay só desenha o conteúdo depois de montado, num segundo desenho: daí o `$nextTick`.
   */
  mounted() {
    this.$nextTick(() => this.smooth())
  }

  smooth() {
    if (this.content && !this.stopSmoothing) this.stopSmoothing = smoothHeight(this.content)
  }

  /**
   * Só a janela ABERTA deixa cópia: se o `model-value` já tinha baixado, o Vuetify fez a saída, e
   * uma segunda seria a janela piscando de volta.
   */
  beforeUnmount() {
    this.stopSmoothing?.()
    const overlay = this.content?.parentElement
    //O `target` é o mesmo de que o Vuetify fez a janela nascer: ela volta para onde veio
    if (overlay?.classList.contains('v-overlay--active')) {
      leaveOverlay(overlay, this.$attrs.target as Element | undefined)
    }
  }
}

export default toNative(Dialog)
</script>

<!-- NÃO scoped: a cópia mora no destino do Teleport, fora do alcance de um estilo scoped. O `inert`
     já tira o foco; isto tira o clique, que o Vuetify devolve ao fundo e ao conteúdo com
     `pointer-events: auto` — sem a regra, a cópia engoliria o clique seguinte enquanto apaga. -->
<style>
.ontos-leaving, .ontos-leaving * {
  pointer-events: none !important;
}
</style>
